import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { api } from "../lib/api";
import { LogoFull } from "../components/Logo";
import { PButton } from "../components/forms";
import { Overline } from "../components/motion";

const FILTERS = ["all", "quote", "studio", "contact"];
const STATUSES = ["new", "contacted", "offered", "closed"];

export default function Admin() {
  const { t } = useLanguage();
  useMeta("Admin — PILATES ON.", "Intern");
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const load = useCallback(
    async (f) => {
      try {
        const params = f !== "all" ? { type: f } : {};
        const r = await api.get("/inquiries", { params });
        setItems(r.data);
      } catch {
        toast.error(t.admin.loadError);
      }
    },
    [t.admin.loadError]
  );

  useEffect(() => {
    if (user) load(filter);
  }, [user, filter, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
    } catch {
      setError(t.admin.loginError);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch {
      toast.error(t.common.formError);
    }
  };

  const exportCsv = () => {
    const cols = ["id", "created_at", "type", "product", "finish", "quantity", "category", "first_name", "last_name", "name", "company", "email", "phone", "location", "items", "opening_date", "message", "status"];
    const esc = (v) => `"${String(Array.isArray(v) ? v.join(", ") : (v ?? "")).replace(/"/g, '""')}"`;
    const csv = [cols.join(";"), ...items.map((i) => cols.map((c) => esc(i[c])).join(";"))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "pilates-on-anfragen.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-[#1C1C1A]" data-testid="admin-loading" />;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1C1C1A] px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md bg-[#FAF8F5] p-10 md:p-12">
          <div className="mb-10 flex items-center gap-3 text-[#1C1C1A]">
            <LogoFull className="h-9 w-auto" variant="dark" />
          </div>
          <Overline className="mb-2">{t.admin.overline}</Overline>
          <h1 data-testid="admin-login-title" className="mb-10 font-serif text-3xl font-light text-[#1C1C1A]">
            {t.admin.loginTitle}
          </h1>
          <form onSubmit={login} className="grid gap-8" noValidate>
            <div>
              <label htmlFor="admin-email" className="mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">
                {t.admin.email}
              </label>
              <input
                id="admin-email"
                data-testid="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-b border-[#1C1C1A]/15 bg-transparent py-3 text-[15px] text-[#1C1C1A] outline-none transition-colors duration-300 focus:border-[#C5A059]"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">
                {t.admin.password}
              </label>
              <input
                id="admin-password"
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-b border-[#1C1C1A]/15 bg-transparent py-3 text-[15px] text-[#1C1C1A] outline-none transition-colors duration-300 focus:border-[#C5A059]"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-[#9A5B4F]">
                {error}
              </p>
            )}
            <PButton type="submit" variant="solid" arrow={false} data-testid="admin-login-submit">
              {t.admin.loginBtn}
            </PButton>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3EFEA]" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1200px] px-6 py-16 md:px-12">
        <div className="mb-12 flex flex-wrap items-center justify-between gap-6">
          <div>
            <Overline className="mb-2">{t.admin.overline}</Overline>
            <h1 data-testid="admin-title" className="font-serif text-4xl font-light text-[#1C1C1A]">
              {t.admin.dashboard}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 border border-[#1C1C1A]/30 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A] transition-colors duration-300 hover:bg-[#1C1C1A] hover:text-[#FAF8F5]"
            >
              <Download className="h-3.5 w-3.5" />
              {t.admin.exportCsv}
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-[#1C1C1A]/30 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A] transition-colors duration-300 hover:bg-[#1C1C1A] hover:text-[#FAF8F5]"
            >
              <LogOut className="h-3.5 w-3.5" />
              {t.admin.logout}
            </button>
          </div>
        </div>

        <div className="mb-10 flex flex-wrap gap-2" data-testid="admin-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                filter === f
                  ? "border-[#1C1C1A] bg-[#1C1C1A] text-[#FAF8F5]"
                  : "border-[#1C1C1A]/20 text-[#66625A] hover:border-[#1C1C1A]"
              }`}
            >
              {f === "all" ? t.admin.filterAll : f === "quote" ? t.admin.filterQuote : f === "studio" ? t.admin.filterStudio : t.admin.filterContact}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <p data-testid="admin-empty" className="py-24 text-center font-serif text-2xl font-light italic text-[#66625A]">
            {t.admin.empty}
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.id} data-testid={`admin-inquiry-${i.id}`} className="bg-[#FAF8F5]">
                <button
                  onClick={() => setOpenId(openId === i.id ? null : i.id)}
                  data-testid={`admin-inquiry-toggle-${i.id}`}
                  className="grid w-full items-center gap-4 px-6 py-5 text-left md:grid-cols-12"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#66625A] md:col-span-2">
                    {new Date(i.created_at).toLocaleDateString("de-DE")}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#C5A059] md:col-span-2">
                    {i.type}
                  </span>
                  <span className="text-sm font-medium text-[#1C1C1A] md:col-span-3">
                    {[i.first_name, i.last_name].filter(Boolean).join(" ") || i.name || "—"}
                  </span>
                  <span className="text-sm font-light text-[#66625A] md:col-span-3">
                    {i.product || (i.items?.length ? i.items.join(", ") : "—")}
                  </span>
                  <span
                    data-testid={`admin-status-${i.id}`}
                    className={`justify-self-start px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] md:col-span-2 ${
                      i.status === "new" ? "bg-[#C5A059]/15 text-[#C5A059]" : "bg-[#1C1C1A]/5 text-[#66625A]"
                    }`}
                  >
                    {t.admin.statuses[i.status] || i.status}
                  </span>
                </button>

                {openId === i.id && (
                  <div className="grid gap-6 border-t border-[#1C1C1A]/10 px-6 py-6 text-sm md:grid-cols-3">
                    <div className="space-y-2 font-light text-[#66625A]">
                      <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">E-Mail: </span>{i.email}</p>
                      {i.phone && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Tel: </span>{i.phone}</p>}
                      {i.company && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Studio: </span>{i.company}</p>}
                      {i.location && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Ort: </span>{i.location}</p>}
                    </div>
                    <div className="space-y-2 font-light text-[#66625A]">
                      {i.finish && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Edition: </span>{i.finish}</p>}
                      {i.quantity && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">{t.admin.quantity}: </span>{i.quantity}</p>}
                      {i.category && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Typ: </span>{i.category}</p>}
                      {i.opening_date && <p><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Termin: </span>{i.opening_date}</p>}
                      {i.message && <p className="pt-2">{i.message}</p>}
                    </div>
                    <div>
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1C1C1A]">Status</p>
                      <select
                        data-testid={`admin-status-select-${i.id}`}
                        value={i.status}
                        onChange={(e) => setStatus(i.id, e.target.value)}
                        className="cursor-pointer border border-[#1C1C1A]/20 bg-transparent px-3 py-2 text-sm outline-none"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {t.admin.statuses[s]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
